import React, { useState } from "react";
import { WhatsappLogo, FacebookLogo, XLogo, Envelope, Link as LinkIcon, Check } from "@phosphor-icons/react";

/**
 * Boutons de partage pour diffuser une alerte doudou perdu.
 * Props:
 *  - url   : URL absolue à partager (string, obligatoire)
 *  - title : texte d'accroche partagé (string, obligatoire)
 */
export default function ShareButtons({ url, title }) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(title);
  const fullText = encodeURIComponent(`${title}\n${url}`);

  const links = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${fullText}`,
      Icon: WhatsappLogo,
      classes: "bg-emerald-500 hover:bg-emerald-600 text-white",
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      Icon: FacebookLogo,
      classes: "bg-blue-600 hover:bg-blue-700 text-white",
    },
    {
      key: "x",
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      Icon: XLogo,
      classes: "bg-stone-900 hover:bg-stone-800 text-white",
    },
    {
      key: "email",
      label: "Email",
      href: `mailto:?subject=${encodeURIComponent("Aidez-moi à retrouver mon doudou")}&body=${fullText}`,
      Icon: Envelope,
      classes: "bg-rose-500 hover:bg-rose-600 text-white",
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      {links.map(({ key, label, href, Icon, classes }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-bold text-sm shadow-md transition-transform hover:-translate-y-0.5 ${classes}`}
          data-testid={`share-${key}`}
          aria-label={`Partager sur ${label}`}
        >
          <Icon size={18} weight="bold" />
          <span className="hidden sm:inline">{label}</span>
        </a>
      ))}
      <button
        type="button"
        onClick={copyLink}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-bold text-sm bg-white border-2 border-stone-200 text-stone-700 hover:border-orange-400 hover:text-orange-600 shadow-sm transition-transform hover:-translate-y-0.5"
        data-testid="share-copy-link"
        aria-label="Copier le lien"
      >
        {copied ? <Check size={18} weight="bold" /> : <LinkIcon size={18} weight="bold" />}
        <span className="hidden sm:inline">{copied ? "Copié !" : "Copier le lien"}</span>
      </button>
    </div>
  );
}
